import React, { useMemo } from 'react';
import { useFormContext, Controller, RegisterOptions } from 'react-hook-form';
import type { InfoField } from '../../../lib';
import useAddTenancyPatient from './useAddTenancyPatient';
import CareMapprDropdown from '../Dropdown/CareMapprDropdown';
import { checkMedicare, getTodayDateString, YYYY_MM_DD } from '../../helpers';

const AUS_PHONE_PATTERN = /^(?:\+?61|0)4\d{8}$/;

interface AddPatientFormInfoProps {
  fields: InfoField[];
}

const AddPatientFormInfo: React.FC<AddPatientFormInfoProps> = ({ fields }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { generateOutsourceOptions } = useAddTenancyPatient();

  const outsourceOptionsMap = useMemo(
    () =>
      fields
        .filter((f) => f.type === 'select-outsource' && f.source)
        .reduce<Record<string, { label: string; value: any }[]>>(
          (acc, f) => ({
            ...acc,
            [f.id]: generateOutsourceOptions(f.source!),
          }),
          {},
        ),
    [fields, generateOutsourceOptions],
  );

  const renderError = (id: string) => {
    const error = errors[id];
    return (
      <div className="invalid-feedback">
        {error?.message?.toString() || 'This field is required.'}
      </div>
    );
  };

  const getValidationRules = (field: InfoField): RegisterOptions => {
    const rules: RegisterOptions = { required: field.required };
    if (field.id === 'phone_no') {
      rules.pattern = {
        value: AUS_PHONE_PATTERN,
        message: 'Invalid phone number format.',
      };
    }
    if (field.id === 'medicare_no') {
      rules.validate = (value: string) => {
        if (value?.length > 0 && !checkMedicare(value)) {
          return 'Invalid Medicare number';
        }
        return true;
      };
    }
    return rules;
  };

  return (
    <div className="container-fluid">
      <h2 className="font-14 font-weight-600 mt-2 mb-4">New patient details</h2>
      <div className="row">
        {fields.map((field) => {
          const isInvalid = !!errors[field.id];
          const commonClass = `form-control ${isInvalid ? 'is-invalid' : ''}`;
          const rules = getValidationRules(field);

          return (
            <div key={field.id} className="col-12 col-md-4 mb-3">
              <label htmlFor={field.id} className="mb-2 labelGrey font-14">
                {field.label}
                {field.required && ' *'}
              </label>

              <Controller
                name={field.id}
                control={control}
                rules={rules}
                render={({ field: ctrl }) => {
                  // Select from external source
                  if (field.type === 'select-outsource') {
                    const options = outsourceOptionsMap[field.id] || [];
                    return (
                      <>
                        <CareMapprDropdown
                          options={options}
                          value={
                            options.find((opt) => opt.value === ctrl.value) ||
                            null
                          }
                          onChange={(selected) =>
                            ctrl.onChange(selected?.value ?? '')
                          }
                          hasError={isInvalid}
                          name={field.id}
                          controlId={field.id}
                          disabled={false}
                          placeholder={`Select ${field.label.toLocaleLowerCase()}`}
                          className={isInvalid ? 'is-invalid' : ''}
                        />
                        {isInvalid && renderError(field.id)}
                      </>
                    );
                  }

                  // Standard select
                  if (field.type === 'select' && field.options) {
                    const options = field.options;
                    return (
                      <>
                        <CareMapprDropdown
                          options={options}
                          value={
                            options.find((opt) => opt.value === ctrl.value) ||
                            null
                          }
                          onChange={(selected) =>
                            ctrl.onChange(selected?.value ?? '')
                          }
                          hasError={isInvalid}
                          name={field.id}
                          controlId={field.id}
                          disabled={false}
                          placeholder={`Select ${field.label.toLocaleLowerCase()}`}
                          className={isInvalid ? 'is-invalid' : ''}
                        />
                        {isInvalid && renderError(field.id)}
                      </>
                    );
                  }

                  // Default input
                  return (
                    <>
                      <input
                        id={field.id}
                        type={field.type}
                        {...ctrl}
                        max={
                          field.type === 'date'
                            ? getTodayDateString(YYYY_MM_DD)
                            : undefined
                        }
                        className={commonClass}
                      />
                      {isInvalid && renderError(field.id)}
                    </>
                  );
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddPatientFormInfo;
